import { cn } from '@h2hspark/ui';
import * as React from 'react';

/**
 * The two pieces of page rhythm every section shares: the band it sits in, and the heading it
 * leads with.
 *
 * They live apart from `sections-panel.tsx` so that the collection sections (`CMS-015`) — which
 * are in their own file, because they fetch — can sit in the same rhythm without importing the
 * renderer that renders them. A section band and the switch that chooses sections are different
 * things, and making one import the other would be a cycle.
 */

/** A band, with the page's rhythm applied once rather than per section. */
export function Band({
  children,
  tint,
  className,
  anchor,
}: {
  readonly children: React.ReactNode;
  readonly tint?: 'plain' | 'sunken' | 'brand';
  readonly className?: string;
  /**
   * The section's own address, so a link elsewhere can point at it — `/#how`.
   *
   * **Authored content, not derived from `_type`.** The prototype's navigation links to `#how`,
   * `#features` and `#events`, and those addresses live in published content and in the settings
   * entry an editor edits. Deriving them from the section's type name would mean a rename in code
   * silently breaking a link somebody wrote in the console.
   *
   * Empty means no `id` at all rather than `id=""`, which is a valid-but-unaddressable element.
   */
  readonly anchor?: string | undefined;
}): React.ReactElement {
  return (
    <section
      {...(anchor !== undefined && anchor !== '' ? { id: anchor } : {})}
      // `scroll-mt` so an anchored section does not land underneath the sticky header, which is
      // the difference between a working anchor and one that appears to jump to the wrong place.
      className={cn(
        'scroll-mt-16',
        'px-6 py-16 sm:py-20',
        tint === 'sunken' && 'bg-muted/40',
        // **`--purple-800`, which is what the design uses — not `--primary`.**
        //
        // `bg-primary` is `--purple-500` (#5c3198), the brand mid-tone. The prototype's closing
        // band and footer are `--purple-800` (#2c164a), which is far darker. Our purple *scale*
        // matches the design exactly; the band was simply pointed at the wrong step of it, and
        // the footer's own comment claimed purple-800 while the class said `bg-primary`. A
        // comment describing an intention the code does not implement is how this survived.
        tint === 'brand' && 'bg-brand-800 text-primary-foreground',
        className,
      )}
    >
      <div className="mx-auto max-w-site">{children}</div>
    </section>
  );
}

/**
 * A section's heading.
 *
 * **h2, never h1**: the hero owns the page's only h1, and a second one gives a reader two
 * competing outlines. `CMS-010` AC-5 is that rule, applied at the one place headings are emitted,
 * and `section-catalogue.test.ts` counts the `h1`s in the renderer to keep it true.
 *
 * An empty heading renders nothing rather than an empty `h2`. That matters most for a collection
 * section, where `CMS-015` AC-5 asks for silence rather than a heading with no items under it.
 */
export function SectionHeading({
  children,
}: {
  readonly children: React.ReactNode;
}): React.ReactElement {
  if (children === '' || children === undefined || children === null) return <></>;
  return <h2 className="text-center text-2xl font-bold tracking-tight sm:text-3xl">{children}</h2>;
}

/**
 * The three lines a band may lead with: the eyebrow, the heading, and the paragraph under it
 * (`CMS-018` AC-3).
 *
 * **All three are optional and each is independently absent.** The prototype's testimonial grid
 * leads with an eyebrow and a heading; its case study grid adds a sentence under the heading; its
 * stat band has none of the three. A component that assumed the heading was the only line is what
 * dropped the other two when the page was transcribed — they were in the design, they were not in
 * the content type, so nobody could have authored them and nobody noticed they were gone.
 *
 * The whole lead-in renders **nothing** when all three are empty, rather than an empty block with
 * a bottom margin: a band with no heading should start at its cards, and the margin below an
 * invisible heading is a gap that reads as a rendering failure.
 *
 * **The space below the lead-in belongs to the lead-in**, which is why the margin is `mb-10` here
 * rather than `mt-10` on the grid that follows. A grid carrying its own top margin puts that gap
 * at the top of a band whose lead-in is empty — the exact phantom gap the paragraph above is
 * about, moved one element down.
 */
export function SectionLeadIn({
  eyebrow,
  heading,
  intro,
}: {
  readonly eyebrow?: string | undefined;
  readonly heading?: string | undefined;
  readonly intro?: string | undefined;
}): React.ReactElement | null {
  const hasEyebrow = eyebrow !== undefined && eyebrow !== '';
  const hasHeading = heading !== undefined && heading !== '';
  const hasIntro = intro !== undefined && intro !== '';
  if (!hasEyebrow && !hasHeading && !hasIntro) return null;

  return (
    <div className="mb-10 text-center">
      {hasEyebrow && <p className="h2h-eyebrow mb-3">{eyebrow}</p>}
      <SectionHeading>{heading}</SectionHeading>
      {hasIntro && (
        <p className={cn('mx-auto max-w-2xl text-lg text-muted-foreground', hasHeading && 'mt-3')}>
          {intro}
        </p>
      )}
    </div>
  );
}
